import {useState, useEffect, useMemo} from 'react';
import {useSharedValue} from '@epic-core/hooks';
import {getSharedContext} from './initializers';
import {AppSharedKeys} from './sharedKeys';
import {cmsApi} from './cms.api';
import {getNoTrailingSlash} from 'epic-node-cms/src/cmsUtils/utils';
import {PageData, CmsMultiData, CmsData, CmsUrlLoadingState} from '../types';
import root from 'window-or-global';
import {isError} from '@epic-mw/error-types';

const defaultLoadingState = {
    loading: false,
    loaded: false,
    error: '',
    emptyPageData: true,
    emptyPageDataList: []
};
let currentLoad = '';

export const useCmsFetch = <P extends PageData>(cmsUrlPattern: string, pageData: P): CmsData<P> => {
    const [urlLoadingState, setUrlLoadingState] = useState<CmsUrlLoadingState>({
        [cmsUrlPattern]: defaultLoadingState
    });
    const loadingState = urlLoadingState[cmsUrlPattern] || defaultLoadingState;
    const [, setCmsStore] = useSharedValue(AppSharedKeys.CmsStore);

    const valid = Boolean(cmsUrlPattern && pageData && Object.keys(pageData).length !== 0);
    const isServer = root.__server_side_render;
    const initSlug = root.__initSlug;
    const isInitialLoad = cmsUrlPattern === initSlug;
    const cmsLoadedEmpty = Boolean(
        (loadingState.loaded || isServer || isInitialLoad) && loadingState.emptyPageData && !valid
    );

    useEffect(() => {
        if (
            (pageData && valid && !loadingState.error) ||
            loadingState.loading ||
            loadingState.loaded ||
            currentLoad === cmsUrlPattern ||
            !cmsUrlPattern ||
            cmsLoadedEmpty
        ) {
            return;
        }
        async function fetchData() {
            try {
                currentLoad = cmsUrlPattern;
                setUrlLoadingState({[cmsUrlPattern]: {loading: true, loaded: false}});
                const fetchedPageData = (await cmsApi.getPageData(cmsUrlPattern)) || ({} as any);
                const {pageSlugParam, cmsPageMap, ...rest} = fetchedPageData;
                const emptyPageData = Object.keys(rest).length === 0;
                const context = getSharedContext();
                const latestCmsStore = context.get(AppSharedKeys.CmsStore);

                setCmsStore(
                    Object.assign({}, latestCmsStore, {
                        [cmsUrlPattern]: rest || {},
                        pageSlugParam,
                        cmsPageMap
                    })
                );
                setUrlLoadingState({
                    [cmsUrlPattern]: {loading: false, loaded: true, emptyPageData}
                });
                currentLoad = '';
            } catch (ex) {
                console.error('Failed to fetch cms data for page', cmsUrlPattern, ex);
                const error: string = (isError(ex) && ex.message) || '';
                setUrlLoadingState({
                    [cmsUrlPattern]: {loading: false, loaded: true, error}
                });
            }
        }
        fetchData();
    }, [pageData, valid, loadingState.loading, loadingState.loaded]);

    return useMemo(() => {
        return {
            pageData,
            cmsLoading:
                loadingState.loading ||
                (!!cmsUrlPattern && !valid && !loadingState.loaded && !isServer && !isInitialLoad),
            cmsLoadedEmpty,
            loaded: loadingState.loaded || valid,
            error: loadingState.error
        };
    }, [
        pageData && pageData._urlPattern,
        loadingState.loading,
        loadingState.loaded,
        isServer,
        isInitialLoad,
        valid,
        loadingState.error
    ]);
};

export const useCmsData = <P extends PageData>(urlPattern: string): CmsData<P> => {
    const [cmsStore] = useSharedValue(AppSharedKeys.CmsStore);
    const cmsUrlPattern = urlPattern.length > 1 ? getNoTrailingSlash(urlPattern) : urlPattern;
    const pageData = cmsStore[cmsUrlPattern] || {};
    delete pageData.pageSlugParam;
    delete pageData.cmsPageMap;

    return useCmsFetch(cmsUrlPattern, pageData);
};

let loadingMulti = false;
export const useMultiCmsData = <P extends PageData>(
    cmsUrlPatterns: string[] = [],
    cmsIds?: string[]
): CmsMultiData<P> => {
    const patternStr = cmsUrlPatterns && cmsUrlPatterns.length ? cmsUrlPatterns.join(',') : '';
    const idStr = cmsIds && cmsIds.length ? cmsIds.join(',') : '';
    const requestId = `${patternStr}|${idStr}`;

    const [urlLoadingState, setUrlLoadingState] = useState<CmsUrlLoadingState>({
        [requestId]: defaultLoadingState
    });
    const loadingState = urlLoadingState[requestId] || defaultLoadingState;

    const [cmsStore, setCmsStore] = useSharedValue(AppSharedKeys.CmsStore);
    const pageData = {};
    let hasPatternPageData = true;
    let hasIdPageData = true;

    let cmsUrlPatternsInvalid = false;
    if (cmsUrlPatterns && cmsUrlPatterns.length) {
        cmsUrlPatterns.forEach((urlPattern) => {
            const value = cmsStore[urlPattern];
            pageData[urlPattern] = value || {};
            if (hasPatternPageData) hasPatternPageData = value && Object.keys(value).length > 0;
        });
    } else {
        cmsUrlPatternsInvalid = true;
    }

    let cmsIdsInvalid = false;
    if (cmsIds && cmsIds.length) {
        cmsIds.forEach((id) => {
            const value = cmsStore[id];
            pageData[id] = value || {};
            if (hasIdPageData) hasIdPageData = value && Object.keys(value).length > 0;
        });
    } else {
        cmsIdsInvalid = true;
    }

    const invalid = cmsUrlPatternsInvalid && cmsIdsInvalid;
    const hasPageData =
        (!cmsUrlPatternsInvalid && hasPatternPageData) || (!cmsIdsInvalid && hasIdPageData);

    useEffect(() => {
        if (
            invalid ||
            hasPageData ||
            loadingState.loaded ||
            loadingState.error ||
            loadingMulti ||
            loadingState.loading
        ) {
            return;
        }
        async function fetchData() {
            try {
                loadingMulti = true;
                setUrlLoadingState({[requestId]: {loading: true, loaded: false}});
                const response = await cmsApi.getBulkPageData({slugs: cmsUrlPatterns, ids: cmsIds});
                const slugs = response.slugs || {};
                const ids = response.ids || {};
                const emptyPageDataList: string[] = [];
                const newStore = {};

                for (const urlPattern in slugs) {
                    if (Object.prototype.hasOwnProperty.call(slugs, urlPattern) && urlPattern) {
                        const pageData = slugs[urlPattern];
                        const emptyPageData = Object.keys(pageData).length === 0;
                        if (emptyPageData) emptyPageDataList.push(urlPattern);
                        newStore[urlPattern] = pageData || {};
                    }
                }
                for (const id in ids) {
                    if (Object.prototype.hasOwnProperty.call(ids, id) && id) {
                        const pageData = ids[id];
                        const emptyPageData = Object.keys(pageData).length === 0;
                        if (emptyPageData) emptyPageDataList.push(id);
                        newStore[id] = pageData || {};
                    }
                }

                const context = getSharedContext();
                const latestCmsStore = context.get(AppSharedKeys.CmsStore);

                setCmsStore(Object.assign({}, latestCmsStore, newStore));
                setUrlLoadingState({[requestId]: {loading: false, loaded: true}});
                loadingMulti = false;
            } catch (ex) {
                console.error(
                    'Failed to fetch cms data for page',
                    cmsUrlPatterns && cmsUrlPatterns.join(', '),
                    ex
                );
                const error: string = (isError(ex) && ex.message) || '';
                setUrlLoadingState({
                    [requestId]: {loading: false, loaded: true, error}
                });
                loadingMulti = false;
            }
        }
        fetchData();
    }, [
        invalid,
        requestId,
        loadingState.loading,
        loadingState.loaded,
        loadingMulti,
        loadingState.error
    ]);

    return {
        pageData,
        cmsLoading: loadingState.loading || (!invalid && !loadingState.loaded && !hasPageData),
        cmsLoadedEmpty:
            loadingState.loaded &&
            loadingState.emptyPageDataList &&
            loadingState.emptyPageDataList.length === cmsUrlPatterns.length,
        loaded: loadingState.loaded,
        error: loadingState.error
    };
};
